import { type FormEvent, useEffect, useMemo, useState } from "react";

import { type ActivityLevel, type BiologicalSex, calculateTdee } from "@sytofood/shared";

import { ApiError, type TargetsView, api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { type AppCopy, appCopy } from "../lib/i18n/app";
import { CrisisSupportFooter } from "./crisis-support-footer";
import { LowTargetSanityModal } from "./low-target-sanity-modal";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, H1, MonoMeta, TopBar } from "./ui/primitives";

type Props = {
  initial?: TargetsView;
  onSaved: () => void;
  onCancel?: () => void;
};

type FormState = {
  kcal: string;
  proteinPct: string;
  fatPct: string;
  carbsPct: string;
  weight: string;
  height: string;
  birthYear: string;
  sex: BiologicalSex | "";
  activity: ActivityLevel | "";
};

const DEFAULT_FORM: FormState = {
  kcal: "2100",
  proteinPct: "30",
  fatPct: "30",
  carbsPct: "40",
  weight: "",
  height: "",
  birthYear: "",
  sex: "",
  activity: "",
};

const ACTIVITY_KEYS: ActivityLevel[] = [
  "sedentary",
  "light",
  "moderate",
  "high",
  "very-high",
];

function activityLabel(level: ActivityLevel, t: AppCopy): string {
  switch (level) {
    case "sedentary":
      return t.targetsActivitySedentary;
    case "light":
      return t.targetsActivityLight;
    case "moderate":
      return t.targetsActivityModerate;
    case "high":
      return t.targetsActivityHigh;
    case "very-high":
      return t.targetsActivityVeryHigh;
  }
}

const MACRO_KEYS = ["protein", "fat", "carbs"] as const;
function macroLabel(key: (typeof MACRO_KEYS)[number], t: AppCopy): string {
  return key === "protein"
    ? t.targetsMacroProtein
    : key === "fat"
      ? t.targetsMacroFat
      : t.targetsMacroCarbs;
}

function macrosFromKcalAndPct(
  kcal: number,
  proteinPct: number,
  fatPct: number,
  carbsPct: number,
): { proteinG: number; fatG: number; carbsG: number } {
  return {
    proteinG: Math.round((kcal * (proteinPct / 100)) / 4),
    fatG: Math.round((kcal * (fatPct / 100)) / 9),
    carbsG: Math.round((kcal * (carbsPct / 100)) / 4),
  };
}

export function TargetsScreen({ initial, onSaved, onCancel }: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  // Sign-out escape hatch — without this, a user who lands on Targets via
  // the mandatory-onboarding branch (onCancel undefined) and hits a save
  // error (401 / network) is trapped: there is no back button and the
  // ZAPISZ CTA keeps failing. Surface a small text link below the CTA.
  const { signOut } = useAuth();
  const [form, setForm] = useState<FormState>(() => {
    if (!initial?.kcalTarget) {
      return {
        ...DEFAULT_FORM,
        weight: initial?.weightKg ? String(initial.weightKg) : "",
        height: initial?.heightCm ? String(initial.heightCm) : "",
        birthYear: initial?.birthYear ? String(initial.birthYear) : "",
        sex: initial?.biologicalSex ?? "",
        activity: initial?.activityLevel ?? "",
      };
    }
    const kcal = initial.kcalTarget;
    const protein = initial.proteinTargetG ?? 0;
    const fat = initial.fatTargetG ?? 0;
    const carbs = initial.carbsTargetG ?? 0;
    return {
      kcal: String(kcal),
      proteinPct: kcal ? String(Math.round(((protein * 4) / kcal) * 100)) : "30",
      fatPct: kcal ? String(Math.round(((fat * 9) / kcal) * 100)) : "30",
      carbsPct: kcal ? String(Math.round(((carbs * 4) / kcal) * 100)) : "40",
      weight: initial.weightKg ? String(initial.weightKg) : "",
      height: initial.heightCm ? String(initial.heightCm) : "",
      birthYear: initial.birthYear ? String(initial.birthYear) : "",
      sex: initial.biologicalSex ?? "",
      activity: initial.activityLevel ?? "",
    };
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sanityModal, setSanityModal] = useState<{ kcal: number } | null>(null);
  const [showDemographics, setShowDemographics] = useState(false);
  const [editingKcal, setEditingKcal] = useState(false);

  const kcal = Number(form.kcal);
  const proteinPct = Number(form.proteinPct);
  const fatPct = Number(form.fatPct);
  const carbsPct = Number(form.carbsPct);
  const macros = Number.isFinite(kcal)
    ? macrosFromKcalAndPct(kcal, proteinPct, fatPct, carbsPct)
    : { proteinG: 0, fatG: 0, carbsG: 0 };
  const pctSum = proteinPct + fatPct + carbsPct;
  const pctOk = pctSum === 100;
  const isLowTarget = Number.isFinite(kcal) && kcal > 0 && kcal < 1200;

  const suggestedTdee = useMemo(
    () =>
      calculateTdee({
        biologicalSex: form.sex || null,
        weightKg: form.weight ? Number(form.weight) : null,
        heightCm: form.height ? Number(form.height) : null,
        birthYear: form.birthYear ? Number(form.birthYear) : null,
        activityLevel: form.activity || null,
      }),
    [form.sex, form.weight, form.height, form.birthYear, form.activity],
  );

  function applyTdee() {
    if (!suggestedTdee) return;
    const rounded = Math.round(suggestedTdee / 50) * 50;
    setForm((f) => ({ ...f, kcal: String(rounded) }));
  }

  async function submit(acknowledgedLowTarget: boolean) {
    setSubmitting(true);
    setError(null);
    try {
      const weightKg = form.weight ? Number(form.weight) : undefined;
      const heightCm = form.height ? Number(form.height) : null;
      const birthYear = form.birthYear ? Number(form.birthYear) : null;
      await api.putTargets({
        kcalTarget: kcal,
        proteinTargetG: macros.proteinG,
        fatTargetG: macros.fatG,
        carbsTargetG: macros.carbsG,
        ...(weightKg ? { weightKg } : {}),
        acknowledgedLowTarget,
        biologicalSex: form.sex || null,
        heightCm,
        birthYear,
        activityLevel: form.activity || null,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === "LOW_TARGET_REQUIRES_ACK") {
        setSanityModal({ kcal });
        return;
      }
      // Print the raw error to the WebView console so a tester reading the
      // Android log (`adb logcat | grep chromium`) sees the stack/status.
      console.error("[TargetsScreen] save failed", err);
      // Surface the status code + message in the red pill so user feedback
      // can be actionable ("401 - Sesja wygasła" is way more useful than a
      // generic "wystąpił błąd").
      const msg =
        err instanceof ApiError
          ? `${err.status} · ${err.message}`
          : err instanceof Error
            ? err.message
            : t.targetsGenericError;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!pctOk) {
      setError(t.targetsPctSumError(pctSum));
      return;
    }
    submit(initial?.acknowledgedLowTarget ?? false);
  }

  useEffect(() => {
    if (pctOk) setError(null);
  }, [pctOk]);

  return (
    <>
      <ScreenShell hideHeader>
        <div style={{ marginBottom: 18 }}>
          <TopBar
            {...(onCancel ? { back: t.targetsBack, onBack: onCancel } : {})}
            title={t.targetsTopTitle}
          />
        </div>

        <H1 accent=".">{t.targetsHeading}</H1>
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>{t.targetsIntro}</p>

        <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: 0 }}>
          {/* kcal target */}
          <section style={{ marginTop: 24 }}>
            <Eyebrow>{t.targetsKcalEyebrow}</Eyebrow>
            <div
              style={{
                marginTop: 10,
                padding: "18px 18px",
                borderRadius: 12,
                background: "var(--surface)",
                boxShadow: "0 0 0 1px var(--nav-border)",
                display: "flex",
                alignItems: "baseline",
                gap: 8,
              }}
            >
              {editingKcal ? (
                <input
                  type="number"
                  inputMode="numeric"
                  required
                  min={800}
                  max={6000}
                  step={50}
                  ref={(el) => {
                    if (el) el.focus();
                  }}
                  value={form.kcal}
                  onChange={(e) => setForm({ ...form, kcal: e.target.value })}
                  onBlur={() => setEditingKcal(false)}
                  className="tabular outline-none bg-transparent"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 54,
                    fontWeight: 800,
                    letterSpacing: "-0.05em",
                    color: "var(--ink)",
                    lineHeight: 1,
                    width: "auto",
                    minWidth: 0,
                    flex: 1,
                    border: "none",
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingKcal(true)}
                  className="tabular"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 54,
                    fontWeight: 800,
                    letterSpacing: "-0.05em",
                    color: "var(--ink)",
                    lineHeight: 1,
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {form.kcal || "0"}
                </button>
              )}
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--accent)",
                  fontWeight: 700,
                }}
              >
                kcal
              </span>
              <button
                type="button"
                onClick={() => setEditingKcal((v) => !v)}
                style={{
                  marginLeft: "auto",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ink-2)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {editingKcal ? t.targetsDone : t.targetsEdit}
              </button>
            </div>
            {suggestedTdee ? (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
                {t.targetsSuggestedTdee(suggestedTdee)} {t.targetsTdeeMethod} ·{" "}
                <button
                  type="button"
                  onClick={applyTdee}
                  style={{
                    color: "var(--accent)",
                    fontWeight: 700,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 11,
                  }}
                >
                  {t.targetsUse}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
                {t.targetsEstimateDisclaimer}
              </div>
            )}
          </section>

          {/* macro split */}
          <section style={{ marginTop: 24 }}>
            <div className="flex items-center justify-between">
              <Eyebrow>{t.targetsMacroEyebrow}</Eyebrow>
              <MonoMeta>
                {pctOk ? (
                  <span style={{ color: "var(--ok-fg)" }}>{t.targetsMacroOk}</span>
                ) : (
                  <span style={{ color: "var(--bad-fg)" }}>{t.targetsMacroBad(pctSum)}</span>
                )}
              </MonoMeta>
            </div>
            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 8,
              }}
            >
              {MACRO_KEYS.map((key) => {
                const fieldKey: keyof FormState =
                  key === "protein" ? "proteinPct" : key === "fat" ? "fatPct" : "carbsPct";
                const grams =
                  key === "protein" ? macros.proteinG : key === "fat" ? macros.fatG : macros.carbsG;
                return (
                  <label
                    key={key}
                    style={{
                      padding: "14px 12px",
                      borderRadius: 12,
                      background: "var(--surface)",
                      boxShadow: "0 0 0 1px var(--nav-border)",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <MonoMeta>{macroLabel(key, t)}</MonoMeta>
                    <div
                      className="tabular flex items-baseline gap-0.5"
                      style={{
                        marginTop: 4,
                        fontFamily: "var(--font-display)",
                        fontSize: 32,
                        fontWeight: 800,
                        letterSpacing: "-0.04em",
                        color: "var(--ink)",
                        lineHeight: 1,
                      }}
                    >
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        value={form[fieldKey]}
                        onChange={(e) => setForm({ ...form, [fieldKey]: e.target.value })}
                        className="tabular outline-none bg-transparent"
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: 32,
                          fontWeight: 800,
                          letterSpacing: "-0.04em",
                          color: "var(--ink)",
                          width: "100%",
                          minWidth: 0,
                          border: "none",
                          padding: 0,
                        }}
                      />
                      <span style={{ fontSize: 14, color: "var(--muted)" }}>%</span>
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                        color: "var(--accent)",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                      }}
                    >
                      = {grams}G
                    </div>
                  </label>
                );
              })}
            </div>
          </section>

          {/* TDEE collapsible */}
          <section style={{ marginTop: 18 }}>
            <button
              type="button"
              onClick={() => setShowDemographics((v) => !v)}
              className="w-full text-left"
              style={{
                padding: "14px 14px",
                borderRadius: 10,
                background: "var(--surface-2)",
                border: "none",
                cursor: "pointer",
              }}
              aria-expanded={showDemographics}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                    {t.targetsAutoTdee}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                    {suggestedTdee
                      ? t.targetsAutoTdeeValue(suggestedTdee)
                      : t.targetsAutoTdeeHint}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    color: "var(--ink-2)",
                    fontWeight: 700,
                  }}
                >
                  {showDemographics ? "−" : "+"}
                </span>
              </div>
            </button>

            {showDemographics ? (
              <div
                style={{
                  marginTop: 10,
                  padding: 14,
                  borderRadius: 12,
                  background: "var(--surface)",
                  boxShadow: "0 0 0 1px var(--nav-border)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <p style={{ fontSize: 11, color: "var(--muted)" }}>
                  {t.targetsDemographicsNote}
                </p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <label className="flex flex-col gap-1">
                    <MonoMeta>{t.targetsSexLabel}</MonoMeta>
                    <select
                      value={form.sex}
                      onChange={(e) =>
                        setForm({ ...form, sex: e.target.value as BiologicalSex | "" })
                      }
                      style={{
                        height: 44,
                        padding: "0 14px",
                        background: "var(--surface)",
                        color: "var(--ink)",
                        borderRadius: 10,
                        boxShadow: "inset 0 0 0 1px var(--nav-border)",
                        // 16px iOS auto-zoom guard.
                        fontSize: 16,
                        border: "none",
                      }}
                    >
                      <option value="">{t.targetsSexNone}</option>
                      <option value="female">{t.targetsSexFemale}</option>
                      <option value="male">{t.targetsSexMale}</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <MonoMeta>{t.targetsBirthYearLabel}</MonoMeta>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1900}
                      max={2025}
                      placeholder={t.targetsBirthYearPlaceholder}
                      value={form.birthYear}
                      onChange={(e) => setForm({ ...form, birthYear: e.target.value })}
                      style={{
                        height: 44,
                        padding: "0 14px",
                        background: "var(--surface)",
                        color: "var(--ink)",
                        borderRadius: 10,
                        boxShadow: "inset 0 0 0 1px var(--nav-border)",
                        // 16px iOS auto-zoom guard.
                        fontSize: 16,
                        border: "none",
                      }}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <MonoMeta>{t.targetsHeightLabel}</MonoMeta>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={80}
                      max={250}
                      step={0.5}
                      placeholder={t.targetsHeightPlaceholder}
                      value={form.height}
                      onChange={(e) => setForm({ ...form, height: e.target.value })}
                      style={{
                        height: 44,
                        padding: "0 14px",
                        background: "var(--surface)",
                        color: "var(--ink)",
                        borderRadius: 10,
                        boxShadow: "inset 0 0 0 1px var(--nav-border)",
                        // 16px iOS auto-zoom guard.
                        fontSize: 16,
                        border: "none",
                      }}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <MonoMeta>{t.targetsWeightLabel}</MonoMeta>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={20}
                      max={300}
                      step={0.1}
                      placeholder={t.targetsWeightPlaceholder}
                      value={form.weight}
                      onChange={(e) => setForm({ ...form, weight: e.target.value })}
                      style={{
                        height: 44,
                        padding: "0 14px",
                        background: "var(--surface)",
                        color: "var(--ink)",
                        borderRadius: 10,
                        boxShadow: "inset 0 0 0 1px var(--nav-border)",
                        // 16px iOS auto-zoom guard.
                        fontSize: 16,
                        border: "none",
                      }}
                    />
                  </label>
                </div>

                <label className="flex flex-col gap-1">
                  <MonoMeta>{t.targetsActivityLabel}</MonoMeta>
                  <select
                    value={form.activity}
                    onChange={(e) =>
                      setForm({ ...form, activity: e.target.value as ActivityLevel | "" })
                    }
                    style={{
                      height: 44,
                      padding: "0 14px",
                      background: "var(--surface)",
                      color: "var(--ink)",
                      borderRadius: 10,
                      boxShadow: "inset 0 0 0 1px var(--nav-border)",
                      // 16px iOS auto-zoom guard.
                      fontSize: 16,
                      border: "none",
                    }}
                  >
                    <option value="">{t.targetsActivityNone}</option>
                    {ACTIVITY_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {activityLabel(k, t)}
                      </option>
                    ))}
                  </select>
                </label>

                {suggestedTdee ? (
                  <button
                    type="button"
                    onClick={applyTdee}
                    className="uppercase"
                    style={{
                      padding: "10px 14px",
                      borderRadius: 10,
                      background: "var(--ink)",
                      color: "var(--bg)",
                      border: "none",
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      cursor: "pointer",
                    }}
                  >
                    {t.targetsUseTdee(suggestedTdee)}
                  </button>
                ) : (
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>{t.targetsFillAll}</p>
                )}
              </div>
            ) : null}
          </section>

          {/* Sanity warning */}
          {isLowTarget ? (
            <div
              style={{
                marginTop: 18,
                padding: "14px 14px",
                borderRadius: 10,
                background: "var(--bad-bg)",
                color: "var(--bad-fg)",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                }}
              >
                {t.targetsSafetyEyebrow}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5 }}>
                {t.targetsSafetyBody}
              </div>
            </div>
          ) : null}

          {error ? (
            <p
              className="text-[12px] px-3 py-2 mt-3 rounded-md"
              style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
            >
              {error}
            </p>
          ) : null}

          {/* Actions */}
          <div style={{ marginTop: 22, display: "flex", gap: 8 }}>
            <button
              type="submit"
              disabled={submitting || !pctOk}
              className="uppercase disabled:opacity-50"
              style={{
                flex: 1,
                padding: "16px 20px",
                borderRadius: 12,
                background: "var(--accent)",
                color: "var(--accent-ink)",
                border: "none",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                boxShadow: "var(--shadow-cta)",
                cursor: "pointer",
              }}
            >
              {submitting ? t.targetsSaving : t.targetsSave}
            </button>
            {onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="uppercase"
                style={{
                  padding: "16px 20px",
                  borderRadius: 12,
                  background: "transparent",
                  color: "var(--ink)",
                  boxShadow: "inset 0 0 0 1px var(--nav-border)",
                  border: "none",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
              >
                {t.targetsCancel}
              </button>
            ) : null}
          </div>
        </form>

        {/* Sign-out escape hatch shown ONLY in the mandatory-onboarding
            flow (no onCancel = no way to leave this screen otherwise).
            A user trapped here by a stale session can break out without
            uninstalling. The button is intentionally low-key — text-only,
            muted — so it doesn't compete with the ZAPISZ CTA. */}
        {!onCancel ? (
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <button
              type="button"
              onClick={() => {
                void signOut();
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                fontSize: 13,
                textDecoration: "underline",
                cursor: "pointer",
                padding: "8px 12px",
              }}
            >
              {t.targetsSignOut}
            </button>
          </div>
        ) : null}

        <div style={{ marginTop: 20 }}>
          <CrisisSupportFooter />
        </div>
      </ScreenShell>

      {sanityModal ? (
        <LowTargetSanityModal
          kcal={sanityModal.kcal}
          onCancel={() => setSanityModal(null)}
          onConfirm={() => {
            setSanityModal(null);
            submit(true);
          }}
        />
      ) : null}
    </>
  );
}
